import { Form, Formik, Field, ErrorMessage, FieldProps } from "formik";
import * as Yup from "yup";
import { Box, Text, Button } from "@radix-ui/themes";
import { CreateUpdateMenuItem, MenuItem } from "@/types/breeze/Menu";
import { formatColumnName } from "@/utils/text";
import { useEntities } from "@/hooks/useCommonQueries";
import InputField from "@/components/form/InputField";
import SelectField from "@/components/form/SelectField";
import { IconPicker } from "@/components/ui/IconPicker";
import { useAuth } from "@/providers/AuthProvider";

const validationSchema = Yup.object({
  display_name: Yup.string().required("This field is required."),
  href: Yup.string().required("This field is required."),
  icon: Yup.string().required("This field is required."),
  type: Yup.string().required("This field is required."),
  parent_id: Yup.number().min(0, "Must be at least 0"),
});

export default function MenuItemForm({
  editingMenuItem,
  allItems,
  isPending,
  onSubmit,
}: {
  editingMenuItem: MenuItem | null;
  allItems: MenuItem[];
  isPending: boolean;
  onSubmit: (values: CreateUpdateMenuItem) => void;
}) {
  const { currentTenant } = useAuth();
  const { data: entities } = useEntities();

  const initialValues: CreateUpdateMenuItem = editingMenuItem
    ? {
        display_name: editingMenuItem.display_name,
        type: editingMenuItem.href.includes("/entities/") ? "entity" : "link",
        href: editingMenuItem.href,
        icon: editingMenuItem.icon,
        parent_id: editingMenuItem.parent_id || 0,
        sort_order: editingMenuItem.sort_order,
      }
    : {
        display_name: "",
        type: "entity",
        href: "",
        icon: "",
        parent_id: 0,
        sort_order: 0,
      };

  return (
    <Box p="4">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue }) => (
          <Form
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-1)",
            }}
          >
            <SelectField name="type" label="Type" required type="string">
              <SelectField.Item value="entity">Entity</SelectField.Item>
              <SelectField.Item value="link">Custom Link</SelectField.Item>
            </SelectField>

            {values.type === "link" ? (
              <InputField name="href" label="Path" required />
            ) : (
              <SelectField
                name="href"
                label="Entity"
                required
                type="string"
                onChange={(e) => {
                  setFieldValue("href", e);
                  setFieldValue(
                    "display_name",
                    formatColumnName(e.toString().split("/").pop()),
                  );
                }}
              >
                {entities
                  ?.filter((e) => !e.is_junction)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((entity) => (
                    <SelectField.Item
                      key={entity.id}
                      value={`/org/${currentTenant?.id}/entities/${entity.name}`}
                    >
                      {entity.name}
                    </SelectField.Item>
                  ))}
              </SelectField>
            )}

            <InputField name="display_name" label="Display name" required />

            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--space-2)",
              }}
            >
              <Text as="label" size="2" weight="medium">
                Icon
              </Text>
              <Field name="icon">
                {({ field, form }: FieldProps) => (
                  <IconPicker
                    value={field.value || ""}
                    onChange={(iconName: string) =>
                      form.setFieldValue("icon", iconName)
                    }
                  />
                )}
              </Field>
              <ErrorMessage name="icon">
                {(msg) => (
                  <Text size="1" color="red">
                    {msg}
                  </Text>
                )}
              </ErrorMessage>
            </Box>

            <SelectField
              name="parent_id"
              label="Parent menu item"
              type="number"
            >
              <SelectField.Item value={0}>
                None (Root Item)
              </SelectField.Item>
              {allItems.map((menuItem) => (
                <SelectField.Item key={menuItem.id} value={menuItem.id}>
                  {menuItem.display_name}
                </SelectField.Item>
              ))}
            </SelectField>

            <Box pt="4">
              <Button type="submit" loading={isPending}>
                {editingMenuItem ? "Update Menu Item" : "Create Menu Item"}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
